/**
 * Gear Suggestions Config Fetcher
 * -------------------------------
 * Isolated fetcher for the panel-managed gear-suggestions envelope
 * (/api/gear-suggestions-config). It never renders anything itself:
 * it fetches + validates the config and, when the master `enabled`
 * flag is true, publishes lookup indexes for the consumers (the
 * easy-suggestion PDP widget and the AGB gear builder):
 *
 *   window.__AGB_GEAR_SUGG = {
 *     rev,          // increments on every publish (repaint/dirty hashes)
 *     byProduct,    // productHandle -> normalized product entry (ALL entries,
 *                   //   including disabled ones -- consumers check the flags)
 *     entryFor,     // (productHandle, baseHandle, productHasHardcodedList) ->
 *                   //   the product entry that wins the BUILDER scope, or null
 *     accessoryFor, // (handle, productHandle, baseHandle) -> that accessory's
 *                   //   object resolved ONLY through the winning entry, or null
 *     baseFor,      // (productHandle, baseHandle) -> that base carrier's art
 *                   //   { front?, back? } from the top-level baseArt map (the
 *                   //   full-frame bottom layer), or null
 *     raw           // the full config document as served
 *   }
 *
 * plus a `document` CustomEvent 'agb:gs-ready' on every publish so
 * consumers can re-render. Consumers treat a missing global as "use the
 * hardcoded maps" — so every failure path here (no URL global, HTTP
 * error, bad JSON, schema mismatch, master disabled) simply never
 * defines the global and the storefront behaves exactly as today.
 *
 * Caching mirrors cart-quantity-cap.js: config from the control panel
 * once per page-load, 5-min ETag-cached in env-namespaced localStorage
 * keys. Endpoint comes from `window.AGB_GEAR_SUGGESTIONS_CONFIG_URL`
 * (set in theme.liquid next to the sibling config globals). The cached
 * config publishes immediately at boot; a fresh 200 re-publishes with
 * rev+1.
 */

(function () {
    'use strict';

    if (typeof window === 'undefined') return;
    if (window.__AGB_GEAR_SUGG_LOADED__) return;
    window.__AGB_GEAR_SUGG_LOADED__ = true;

    const LOG_PREFIX = '[gear-suggestions]';
    const CONFIG_TTL_MS = 5 * 60 * 1000;

    // Live vs Preview storefront detection (mirrored from
    // cart-quantity-cap.js / favorite-color-variant-picker.js). Each engine
    // file is self-contained so it can load in any order. See the long
    // comment there for what each branch does and why preview is the safe
    // default.
    function _agbDetectStorefrontEnv() {
        try {
            const urlForce = new URLSearchParams(location.search).get('storefront_env');
            if (urlForce === 'preview' || urlForce === 'live') return urlForce;
        } catch (_) {}
        try {
            const stored = localStorage.getItem('AGB_StorefrontEnv');
            if (stored === 'preview' || stored === 'live') return stored;
        } catch (_) {}
        const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
        if (role === 'main') return 'live';
        if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
        const host = (location && location.hostname) || '';
        if (host.endsWith('.shopifypreview.com')) return 'preview';
        if (host.endsWith('.myshopify.com')) return 'preview';
        if (host === '127.0.0.1' || host === 'localhost') return 'preview';
        try {
            if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
        } catch (_) {}
        return 'preview';
    }

    const GEAR_SUGG_ENV = _agbDetectStorefrontEnv();
    const LS_CONFIG_KEY = `AGB_GearSuggestions_${GEAR_SUGG_ENV}`;
    const LS_CONFIG_ETAG = `AGB_GearSuggestionsEtag_${GEAR_SUGG_ENV}`;
    const LS_CONFIG_AT = `AGB_GearSuggestionsFetchedAt_${GEAR_SUGG_ENV}`;

    // Diagnostics print on PREVIEW only — never on live (no console noise).
    // Honors the panel's dev-logs category toggle:
    // window.__AGB_consoleCategories.gear_suggestions — undefined = on
    // (default), explicit false = silenced (same convention as the other
    // log categories, e.g. custom-labels' `aco`).
    function _dbg() {
        try {
            if (GEAR_SUGG_ENV !== 'preview') return;
            const cats = window.__AGB_consoleCategories;
            if (cats && typeof cats === 'object' && cats.gear_suggestions === false) return;
            console.log.apply(console, arguments);
        } catch (e) {}
    }

    // ===== Publish ============================================================

    let publishRev = 0;

    /**
     * Normalize one accessory at index-build time so every consumer gets a
     * sane shape no matter what the cached/served document contained:
     *   - the accessory must be a plain object with a non-empty string
     *     handle (trimmed), else it is dropped;
     *   - `canvas` is coerced to a plain object;
     *   - a placement (default or per-base) missing a finite x/y/w is
     *     dropped entirely, so the painter falls back to the letterbox fit
     *     instead of feeding NaN into drawImage.
     */
    function normalizeAccessory(a) {
        if (!a || typeof a !== 'object' || Array.isArray(a)) return null;
        const handle = (typeof a.handle === 'string') ? a.handle.trim() : '';
        if (!handle) return null;
        const canvasRaw = a.canvas;
        const canvas = (canvasRaw && typeof canvasRaw === 'object' && !Array.isArray(canvasRaw))
            ? Object.assign({}, canvasRaw)
            : {};
        const isFinitePlacement = (p) => !!p && typeof p === 'object' &&
            Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.w);
        if (!isFinitePlacement(canvas.placement)) delete canvas.placement;
        if (canvas.placementsByBase && typeof canvas.placementsByBase === 'object' && !Array.isArray(canvas.placementsByBase)) {
            const byBase = {};
            for (const k of Object.keys(canvas.placementsByBase)) {
                if (isFinitePlacement(canvas.placementsByBase[k])) byBase[k] = canvas.placementsByBase[k];
            }
            canvas.placementsByBase = byBase;
        } else {
            delete canvas.placementsByBase;
        }
        return Object.assign({}, a, { handle: handle, canvas: canvas });
    }

    /**
     * Normalize one base carrier's art — the shape stored in the config's
     * top-level `baseArt` map, keyed by base handle:
     *   { front?: {color:url}, back?: {color:url} }
     * The base is the full-frame BOTTOM layer the accessories composite onto,
     * colour-matched to the selected base variant. Returns null unless at least
     * one view carries a usable image, so a base with no art falls through to
     * the legacy PRODUCT_LAYER_MAP path (or, for a net-new base, paints nothing
     * rather than a white block).
     */
    function normalizeBaseArt(a) {
        if (!a || typeof a !== 'object' || Array.isArray(a)) return null;
        const viewMap = (raw) => {
            if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
            const out = {};
            for (const k of Object.keys(raw)) {
                const v = raw[k];
                if (typeof v === 'string' && v.trim()) out[k] = v.trim();
            }
            return Object.keys(out).length ? out : null;
        };
        const front = viewMap(a.front);
        const back = viewMap(a.back);
        if (!front && !back) return null;
        const out = {};
        if (front) out.front = front;
        if (back) out.back = back;
        return out;
    }

    /**
     * Build the base-art index from the config's top-level `baseArt` map:
     *   baseHandle -> normalized { front?, back? }  (only bases with real art).
     * baseArt was historically panel-editor-only; the builder now reads it as
     * the base layer for a net-new base (see makeBaseFor / the canvas painter).
     */
    function buildBaseIndex(cfg) {
        const out = {};
        const src = (cfg && cfg.baseArt && typeof cfg.baseArt === 'object' && !Array.isArray(cfg.baseArt))
            ? cfg.baseArt : {};
        for (const k of Object.keys(src)) {
            const handle = (typeof k === 'string') ? k.trim() : '';
            if (!handle) continue;
            const norm = normalizeBaseArt(src[k]);
            if (norm) out[handle] = norm;
        }
        return out;
    }

    /**
     * Build the consumer lookup index from a validated config document.
     *   byProduct — productHandle -> its NORMALIZED product entry. ALL entries
     *               are indexed, including disabled ones: entryFor needs to
     *               see a present-but-disabled entry to pin that product to
     *               its hardcoded list (it must NOT fall through to the base
     *               handle's entry), and every consumer re-checks the
     *               enabled/scope flags anyway. `accessories` is always an
     *               array of objects with string handles (see
     *               normalizeAccessory), so consumers can .filter/.map it
     *               without shape guards.
     */
    function buildIndexes(cfg) {
        const byProduct = {};
        const products = Array.isArray(cfg.products) ? cfg.products : [];
        for (const p of products) {
            if (!p || typeof p !== 'object') continue;
            const handle = (typeof p.productHandle === 'string') ? p.productHandle.trim() : '';
            if (!handle || Object.prototype.hasOwnProperty.call(byProduct, handle)) continue;
            const accessoriesRaw = Array.isArray(p.accessories) ? p.accessories : [];
            const accessories = [];
            for (const a of accessoriesRaw) {
                const normalized = normalizeAccessory(a);
                if (normalized) accessories.push(normalized);
            }
            byProduct[handle] = Object.assign({}, p, { accessories: accessories });
        }
        return { byProduct: byProduct };
    }

    /**
     * Resolve the product entry that wins the BUILDER scope for a
     * product/base handle pair. ALL the precedence lives here — the caller
     * only contributes the one fact it alone knows (whether the product
     * handle carries its own hardcoded builder list), passed as the optional
     * `productHasHardcodedList` boolean. Chosen over the
     * return-{entry,matchedBy} alternative so no consumer can re-implement
     * the precedence differently.
     *
     * Precedence (mirrors the hardcoded path's bundle handling):
     *   1. byProduct[productHandle] when enabled + builderEnabled -> wins.
     *   2. a PRESENT product entry that is disabled (or not builder-scoped)
     *      pins the product to its hardcoded list -> null. It never falls
     *      through to the base handle's entry.
     *   3. productHasHardcodedList (e.g. a bundle with its own hardcoded
     *      builder list) also stops the fall-through -> null.
     *   4. byProduct[baseHandle] when enabled + builderEnabled -> wins.
     *   5. otherwise null (hardcoded fallback).
     */
    function makeEntryFor(byProduct) {
        return function entryFor(productHandle, baseHandle, productHasHardcodedList) {
            const productEntry = (productHandle && Object.prototype.hasOwnProperty.call(byProduct, productHandle))
                ? byProduct[productHandle]
                : null;
            if (productEntry) {
                return (productEntry.enabled === true && productEntry.builderEnabled === true)
                    ? productEntry
                    : null;
            }
            if (productHasHardcodedList) return null;
            const baseEntry = (baseHandle && Object.prototype.hasOwnProperty.call(byProduct, baseHandle))
                ? byProduct[baseHandle]
                : null;
            if (baseEntry && baseEntry.enabled === true && baseEntry.builderEnabled === true) {
                return baseEntry;
            }
            return null;
        };
    }

    /**
     * Resolve an accessory's object ONLY through the entry that wins for the
     * given product/base scope — one product's accessory settings (canvas
     * mode/art/placement, isPlate, ...) must never leak onto another
     * product's builder. Returns null when no entry wins or the handle is
     * not in the winning entry. `productHandle` may be null (image-layer
     * callers that can't read the builder state); the base entry is then the
     * only candidate. Callers here can't know about hardcoded lists, so
     * entryFor's productHasHardcodedList is omitted — acceptable: the scope
     * is still bounded to the CURRENT product/base pair.
     */
    function makeAccessoryFor(entryFor) {
        return function accessoryFor(handle, productHandle, baseHandle) {
            if (!handle || typeof handle !== 'string') return null;
            const entry = entryFor(productHandle || null, baseHandle || null);
            if (!entry || !Array.isArray(entry.accessories)) return null;
            for (const a of entry.accessories) {
                if (a && a.handle === handle) return a;
            }
            return null;
        };
    }

    /**
     * Resolve a builder's BASE art ({ front?, back? }) for the given
     * product/base scope, or null. Reads the top-level baseArt index by base
     * handle, then by product handle. Base art is inherently keyed by the base
     * carrier, so it isn't entry-scoped — but it only ever REACHES the canvas
     * for a product that has a winning builder entry (buildPaintOrder gates on
     * entryFor) AND whose base has no baked PRODUCT_LAYER_MAP art (the images
     * resolver lets baked carriers win). So legacy carriers are never affected.
     */
    function makeBaseFor(baseByHandle) {
        return function baseFor(productHandle, baseHandle) {
            const bh = (typeof baseHandle === 'string') ? baseHandle.trim() : '';
            const ph = (typeof productHandle === 'string') ? productHandle.trim() : '';
            if (bh && Object.prototype.hasOwnProperty.call(baseByHandle, bh)) return baseByHandle[bh];
            if (ph && Object.prototype.hasOwnProperty.call(baseByHandle, ph)) return baseByHandle[ph];
            return null;
        };
    }

    /**
     * Publish a config to the consumers. No-ops (leaving any previous publish
     * in place) unless the document is schemaVersion 1 with the master
     * `enabled` flag on — a disabled/invalid config must never define the
     * global, so consumers keep their hardcoded fallback.
     */
    function publishConfig(cfg) {
        try {
            if (!cfg || typeof cfg !== 'object') return;
            if (cfg.schemaVersion !== 1) return;
            if (cfg.enabled !== true) return;
            const indexes = buildIndexes(cfg);
            const baseByHandle = buildBaseIndex(cfg);
            const entryFor = makeEntryFor(indexes.byProduct);
            publishRev += 1;
            window.__AGB_GEAR_SUGG = {
                rev: publishRev,
                byProduct: indexes.byProduct,
                entryFor: entryFor,
                accessoryFor: makeAccessoryFor(entryFor),
                baseFor: makeBaseFor(baseByHandle),
                raw: cfg
            };
            document.dispatchEvent(new CustomEvent('agb:gs-ready'));
            _dbg(LOG_PREFIX, 'published rev', publishRev, '—',
                Object.keys(indexes.byProduct).length, 'product(s), env=' + GEAR_SUGG_ENV);
        } catch (e) { /* fail-safe: consumers keep their fallback */ }
    }

    // ===== Config loading =====================================================

    function loadCachedConfig() {
        try {
            const raw = localStorage.getItem(LS_CONFIG_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.schemaVersion === 1) {
                publishConfig(parsed);
            }
        } catch (e) { /* ignore */ }
    }

    async function fetchConfigFromAPI() {
        const base = window.AGB_GEAR_SUGGESTIONS_CONFIG_URL;
        if (!base) {
            _dbg(LOG_PREFIX, 'no AGB_GEAR_SUGGESTIONS_CONFIG_URL set -- skipping fetch');
            return;
        }
        const endpoint = GEAR_SUGG_ENV === 'preview'
            ? `${base}${base.indexOf('?') === -1 ? '?' : '&'}env=preview`
            : base;
        try {
            const lastFetched = parseInt(localStorage.getItem(LS_CONFIG_AT) || '0', 10);
            if (Date.now() - lastFetched < CONFIG_TTL_MS) return;

            const etag = localStorage.getItem(LS_CONFIG_ETAG);
            const headers = { 'Accept': 'application/json' };
            if (etag) headers['If-None-Match'] = etag;
            // Send this store's region explicitly when the theme declares it
            // (window.AGB_STORE_REGION): a preview theme runs on a myshopify /
            // shopifypreview host the backend can't map from the domain, so
            // without it the EU/IL preview fetches the ROW config.
            try {
                const rgn = window.AGB_STORE_REGION || '';
                if (rgn) headers['X-Store-Region'] = rgn;
            } catch (e) {}

            const res = await fetch(endpoint, { headers });
            localStorage.setItem(LS_CONFIG_AT, String(Date.now()));
            if (res.status === 304) return; // cached copy already published at boot
            if (!res.ok) return;

            const body = await res.json();
            if (!body || !body.config) return;
            const newCfg = body.config;
            if (newCfg.schemaVersion !== 1) return;

            // Persist whatever valid document the panel served — including a
            // master-disabled one, so the NEXT page load correctly falls back
            // to hardcoded. publishConfig below still refuses to publish it.
            localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(newCfg));
            const newEtag = res.headers.get('ETag');
            if (newEtag) localStorage.setItem(LS_CONFIG_ETAG, newEtag);

            publishConfig(newCfg); // fresh 200 re-publishes with rev+1
        } catch (e) { /* network failure: keep cached config */ }
    }

    // ===== Init ===============================================================

    function init() {
        loadCachedConfig();
        // Async refresh; consumers re-render on 'agb:gs-ready' as data arrives.
        fetchConfigFromAPI();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
